import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail, Lock } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_0e5f78cf-f454-4b25-b923-9c08cfb591e4/artifacts/lmgq6k8j_file_00000000ca9471f4a6a1a00d93bbed3f.png";

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Bienvenue sur Era Lodge');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Identifiants incorrects');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Hero Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1750271329214-a7dbce880e85?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80"
          alt="Villa de luxe"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
        <div className="relative z-10 flex flex-col justify-end p-8 md:p-12 text-white">
          <div className="mb-6">
            <img src={LOGO_URL} alt="Era Lodge" className="h-16 md:h-20 w-auto" />
          </div>
          <p className="text-base md:text-lg text-white/80 max-w-md leading-relaxed">
            Gérez vos locations courte durée. Suivez vos revenus, réservations et dépenses.
          </p>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-4 md:p-8 bg-background">
        <div className="w-full max-w-md animate-fade-in">
          <div className="mb-8 lg:hidden flex justify-center">
            <img src={LOGO_URL} alt="Era Lodge" className="h-12 md:h-14 w-auto" />
          </div>

          <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
            <CardHeader className="space-y-1 pb-4 md:pb-6">
              <CardTitle className="font-heading text-xl md:text-2xl tracking-tight">Connexion</CardTitle>
              <CardDescription className="text-muted-foreground">
                Connectez-vous à votre compte
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="nom@exemple.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 bg-muted/50 border-input focus:border-primary h-11"
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="Votre mot de passe"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 bg-muted/50 border-input focus:border-primary h-11"
                      required
                      data-testid="login-password-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow transition-all duration-300"
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? 'Connexion...' : 'Se connecter'}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <Link to="/admin/forgot-password" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Mot de passe oublié ?
                </Link>
              </div>

              <div className="mt-4 text-center text-sm text-muted-foreground">
                Pas encore de compte ?{' '}
                <Link to="/admin/register" className="text-primary hover:text-primary/80 font-medium" data-testid="register-link">
                  Créer un compte
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
